import React, { Fragment, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogDescription,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { ExclamationIcon, StatusOfflineIcon } from "@heroicons/react/outline";
import { Button } from "../Button";
import { classNames } from "../../Utils";
import { useServiceAvailability } from "../../Utils/useServiceAvailability";
import { useTranslation } from "react-i18next";

const MaintenanceModal = ({
  isVisible = false,
  onClose = () => {},
  onChangeAvailability = () => {},
  onChangeStatus = () => {},
  configValue,
}) => {
  const { t } = useTranslation();

  // Re-evaluated every second, so availability flips by itself at the
  // opening/closing boundary rather than only when the config changes.
  const { config, serviceAvailable, secondsLeft } =
    useServiceAvailability(configValue);

  useEffect(() => {
    onChangeAvailability(serviceAvailable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceAvailable]);

  useEffect(() => {
    if (config.status) {
      onChangeStatus(config.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.status]);

  const formatTime = (secs) => {
    const hours = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const secsRemaining = secs % 60;
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(
      2,
      "0"
    )}:${String(secsRemaining).padStart(2, "0")}`;
  };

  const title = useMemo(() => {
    let contentTitle = "serviceMaintenance";

    switch (config.status) {
      case "SCHEDULED_ACTIVE":
      case "OFFLINE":
      case "SCHEDULED_OFFLINE":
        contentTitle = "serviceUnavailable";
        break;
      default:
    }

    return contentTitle;
  }, [config.status]);

  const description = useMemo(() => {
    let contentDescription = t("maintenanceMessage");
    let timeLeftDescription = ` ${t("estimatedCompletionTime")}: `;

    switch (config.status) {
      case "SCHEDULED_ACTIVE":
      case "OFFLINE":
      case "SCHEDULED_OFFLINE":
        contentDescription = t("serviceUnavailableMessage");
        timeLeftDescription = ` ${t("estimatedAvailableTime")}: `;
        break;
      default:
    }

    contentDescription += ` ${t("apologyMessage")}`;

    if (secondsLeft > 0) {
      contentDescription += timeLeftDescription;
    }

    return contentDescription;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.status, secondsLeft]);

  const { iconColor, bgColor } = useMemo(() => {
    let iconColor = "text-yellow-400";

    if (
      config.status === "OFFLINE" ||
      config.status === "SCHEDULED_OFFLINE" ||
      config.status === "SCHEDULED_ACTIVE"
    ) {
      iconColor = "text-gray-400";
    }

    const bgColor = iconColor.replace("text-", "bg-").replace("400", "100");

    return { iconColor, bgColor };
  }, [config.status]);

  return (
    <Transition show={isVisible}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={onClose}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop
              transition
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            />
          </TransitionChild>
          {/* Headless UI marks the backdrop aria-hidden, so the content cannot
              live inside it. This box is the one the backdrop used to give it. */}
          <div className="fixed inset-0">
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
                <div
                  className={classNames(
                    bgColor,
                    "mx-auto flex items-center justify-center h-12 w-12 rounded-full"
                  )}
                >
                  {config.status === "OFFLINE" ||
                  config.status === "SCHEDULED_OFFLINE" ||
                  config.status === "SCHEDULED_ACTIVE" ? (
                    <StatusOfflineIcon
                      className={`h-6 w-6 ${iconColor}`}
                      aria-hidden="true"
                    />
                  ) : (
                    <ExclamationIcon
                      className={`h-6 w-6 ${iconColor}`}
                      aria-hidden="true"
                    />
                  )}
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    {t(title)}
                  </DialogTitle>
                  <div className="mt-2">
                    <DialogDescription as="p" className="text-sm text-gray-500">
                      {description}
                    </DialogDescription>
                  </div>
                </div>
                {secondsLeft > 0 && (
                  <div className="flex justify-center mt-4">
                    <span
                      className={classNames(
                        bgColor,
                        "text-lg font-semibold p-2 rounded-lg items-center"
                      )}
                    >
                      {formatTime(secondsLeft)}
                    </span>
                  </div>
                )}

                <div className="mt-5 sm:mt-6">
                  <Button
                    type="button"
                    className="w-full"
                    onClick={onClose}
                    title="understand"
                  />
                </div>
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MaintenanceModal;
